// apps/web/src/lib/painel/home/catalogo-metricas.ts — Fase 17.8, Planos 04 e 06 (D-05, D-06, D-10; UI-SPEC U-27).
//
// Rótulo, definição, destino e ícone por id de métrica: a fonte única dos textos que o popover de medição, o link
// "Ver lista" e os kv de apoio mostram. Os planos de perfil seguintes acrescentam os ids das suas métricas. `icone` é o
// nome do ícone Lucide (o componente resolve o nome; aqui fica só dado, sem import de componente).

import type { Cobertura, MetricaHeroi, PerfilHome, ValorSecundario } from '@pieltec/contracts';
import { formatarMoedaCompacta, formatarNumero, horaSaoPaulo, nomeDoMes } from './formato';

/** O que o texto de apoio de um kv pode usar: os números secundários e a cobertura da métrica. */
export interface InsumosDoApoio {
  readonly secundarios?: readonly ValorSecundario[];
  readonly cobertura?: Cobertura;
}

export interface EntradaMetrica {
  readonly rotulo: string;
  readonly definicao: string;
  /** Rota interna fixa aberta pelo "Ver lista"; só renderiza se a sessão puder abrir a rota (`podeAbrirRota`). */
  readonly destino?: string;
  readonly icone: string;
  /** Sufixo do período ao lado do rótulo de um kv de apoio (ex.: '· 30 dias'). */
  readonly sufixoPeriodo?: string;
  /** Posição 1..3 do kv no bloco de apoio do perfil: define o chip (cor e tinta). Estável mesmo se outro kv for omitido. */
  readonly chip?: 1 | 2 | 3;
  /** Linha de apoio do kv (até 2 linhas); `undefined` = sem apoio. Monta o texto a partir dos secundários e da cobertura. */
  readonly textoApoio?: (insumos: InsumosDoApoio) => string | undefined;
  /** Legenda da sparkline do herói (padrão 'últimos 14 dias úteis'). */
  readonly rotuloSerie?: string;
  /** Valor maior que zero é um alerta (número em vermelho por CSS; o kv leva `data-alerta`, então a cor nunca é o único sinal). */
  readonly alertaSeMaiorQueZero?: boolean;
  /** Valor maior que zero é um aviso (número em --color-warning por CSS; o kv leva `data-aviso`, então a cor nunca é o único sinal). */
  readonly avisoSeMaiorQueZero?: boolean;
  /** Rótulo montado com os números secundários da resposta (ex.: o limite de dias da NC antiga); sem ele vale `rotulo`. */
  readonly rotuloDe?: (insumos: InsumosDoApoio) => string;
  /** Linha de apoio de um kv em SEM_MEDICAO (a definição do porquê não há número); sem ela vale "Sem medição". */
  readonly textoSemMedicao?: string;
}

function numeroSecundario(insumos: InsumosDoApoio, id: string): number | undefined {
  const valor = insumos.secundarios?.find((secundario) => secundario.id === id)?.valor;
  if (valor === undefined) return undefined;
  const numero = typeof valor === 'number' ? valor : Number(valor);
  return Number.isFinite(numero) ? numero : undefined;
}

function textoSecundario(insumos: InsumosDoApoio, id: string): string | undefined {
  const valor = insumos.secundarios?.find((secundario) => secundario.id === id)?.valor;
  return typeof valor === 'string' && valor !== '' ? valor : undefined;
}

/** Fila de abastecimento já filtrada em "A separar" (o um toque do tablet: a tela lê `?estado=` da URL). */
const DESTINO_FILA_A_SEPARAR = '/estoque/abastecimento?estado=SOLICITADA';

export const CATALOGO_METRICAS: Readonly<Record<string, EntradaMetrica>> = {
  'area-pintada-hoje': {
    rotulo: 'Área pintada hoje',
    definicao:
      'Soma das peças apontadas hoje nas cabines (boas e não conformes) multiplicada pela área unitária do item. Compara com a média dos 14 dias úteis anteriores; não há meta cadastrada.',
    // O relatório A04 não tem rota própria (é uma métrica do índice de relatórios): o destino é a visão geral de monitoramento.
    destino: '/monitoramento/visao-geral',
    icone: 'PaintBucket',
  },
  'devolvidas-no-prazo-30d': {
    rotulo: 'Devolvidas no prazo',
    sufixoPeriodo: '· 30 dias',
    definicao:
      'Percentual das peças devolvidas nos últimos 30 dias cuja devolução ocorreu até a previsão de entrega do item do pedido de venda. Itens sem previsão de entrega ficam fora do cálculo e entram na cobertura. Compara com os 30 dias anteriores, em pontos percentuais.',
    // A lista de pedidos de venda não tem filtro por previsão na URL: o destino é a lista inteira.
    destino: '/vendas/pedidos',
    icone: 'CalendarCheck',
    chip: 1,
    textoApoio: ({ cobertura }) => {
      if (cobertura === undefined) return undefined;
      const itens = cobertura.total === 1 ? 'item' : 'itens';
      return `${formatarNumero(cobertura.base)} de ${formatarNumero(cobertura.total)} ${itens} com previsão de entrega`;
    },
  },
  'em-producao': {
    rotulo: 'Em produção',
    definicao:
      'Ordens de produção liberadas ou em produção agora. O lead time médio é o tempo médio entre a criação e a conclusão no Kanban das OPs concluídas nos últimos 30 dias.',
    destino: '/planejamento/kanban',
    icone: 'Factory',
    chip: 2,
    textoApoio: (insumos) => {
      const dias = numeroSecundario(insumos, 'lead-time-medio-30d');
      if (dias === undefined) return undefined;
      return `lead time médio ${formatarNumero(dias, 1)} ${dias === 1 ? 'dia' : 'dias'}`;
    },
  },
  'retrabalho-7d': {
    rotulo: 'Retrabalho',
    sufixoPeriodo: '· 7 dias',
    definicao:
      'Percentual das peças apontadas nas cabines nos últimos 7 dias que voltaram como não conformidade destinada a retrabalho ou refazer. Compara com os 7 dias anteriores, em pontos percentuais; quanto menor, melhor.',
    destino: '/qualidade/nao-conformidades',
    icone: 'RotateCcw',
    chip: 3,
    rotuloSerie: 'retrabalho por dia · 14 dias úteis',
    textoApoio: (insumos) => {
      const lotes = numeroSecundario(insumos, 'lotes-repintados');
      if (lotes === undefined) return undefined;
      return lotes === 1 ? '1 lote repintado' : `${formatarNumero(lotes)} lotes repintados`;
    },
  },
  'ncs-abertas': {
    rotulo: 'Não conformidades abertas',
    definicao: 'Não conformidades ainda sem destinação da Qualidade (retrabalho, refazer ou sucata), de qualquer idade.',
    destino: '/qualidade/nao-conformidades',
    icone: 'ShieldAlert',
    chip: 1,
    textoApoio: () => 'aguardando destinação',
  },
  'ncs-antigas': {
    rotulo: 'NCs abertas há mais tempo',
    // O limite em dias vem da resposta (secundário `limite-dias`): a tela nunca o escreve por conta própria.
    rotuloDe: (insumos) => {
      const dias = numeroSecundario(insumos, 'limite-dias');
      return dias === undefined ? 'NCs abertas há mais tempo' : `NCs abertas há mais de ${formatarNumero(dias)} ${dias === 1 ? 'dia' : 'dias'}`;
    },
    definicao: 'Não conformidades ainda sem destinação da Qualidade, abertas há mais do que o limite de dias indicado no rótulo. Um número acima de zero pede atenção.',
    destino: '/qualidade/nao-conformidades',
    icone: 'Hourglass',
    chip: 2,
    avisoSeMaiorQueZero: true,
  },
  retrabalhos: {
    rotulo: 'Retrabalhos em andamento',
    definicao:
      'Não conformidades destinadas a retrabalho ou refazer cuja ordem de serviço corretiva ainda não terminou (as etapas Destinada e Em retrabalho da esteira, somadas).',
    destino: '/qualidade/nao-conformidades',
    icone: 'RotateCcw',
    chip: 3,
  },
  'po-entregue-hoje': {
    rotulo: 'Pó entregue às cabines hoje',
    definicao:
      'Consumo líquido de tinta em pó hoje, em kg: saídas de pó menos devoluções, já descontados os estornos, só do estoque próprio da Pieltec, pelo dia operacional do movimento. Perdas não entram. Compara com a média dos 14 dias úteis anteriores; não há meta cadastrada.',
    destino: '/estoque/consumo-tinta',
    icone: 'Boxes',
    rotuloSerie: 'pó entregue por dia · 14 dias úteis',
  },
  'fila-separacao': {
    rotulo: 'Na fila de separação',
    definicao:
      'Solicitações de abastecimento das cabines ainda por separar (estado "A separar"), a mesma contagem da fila do tablet. O apoio mostra há quantos minutos espera a mais antiga.',
    destino: DESTINO_FILA_A_SEPARAR,
    icone: 'ClipboardList',
    chip: 1,
    textoApoio: (insumos) => {
      const minutos = numeroSecundario(insumos, 'mais-antigo-min');
      return minutos === undefined ? undefined : `o mais antigo espera há ${formatarNumero(minutos)} min`;
    },
  },
  'notas-compra-conferir': {
    rotulo: 'Notas de compra a conferir',
    definicao: 'Notas de compra importadas que ainda aguardam a conferência do recebimento. O apoio conta as que chegaram hoje.',
    destino: '/compras/notas',
    icone: 'FileCheck',
    chip: 2,
    textoApoio: (insumos) => {
      const hoje = numeroSecundario(insumos, 'chegaram-hoje');
      if (hoje === undefined || hoje <= 0) return undefined;
      return hoje === 1 ? '1 chegou hoje' : `${formatarNumero(hoje)} chegaram hoje`;
    },
  },
  'pedidos-em-aberto': {
    rotulo: 'Pedidos em aberto',
    definicao:
      'Pedidos de compra não cancelados com ao menos um item ainda por receber. Quem tem permissão para valores vê o saldo a receber em reais (quantidade ainda não recebida vezes o preço do item); quem não tem vê a contagem de pedidos. A faixa separa os pedidos pelo prazo de entrega: no prazo, vencem em 7 dias e atrasados. Não há meta cadastrada.',
    destino: '/compras/pedidos',
    icone: 'ShoppingCart',
    rotuloSerie: 'pedidos emitidos por dia · 14 dias úteis',
  },
  'pedidos-atrasados': {
    rotulo: 'Pedidos de compra atrasados',
    definicao: 'Pedidos de compra em aberto com algum item ainda por receber cujo prazo de entrega já passou. Um número acima de zero pede atenção.',
    destino: '/compras/pedidos',
    icone: 'AlertTriangle',
    chip: 1,
    alertaSeMaiorQueZero: true,
  },
  'materiais-abaixo-minimo': {
    rotulo: 'Materiais abaixo do mínimo',
    definicao: 'Materiais com estoque mínimo definido cujo saldo próprio da Pieltec está abaixo desse mínimo. Sem nenhum mínimo definido não há o que medir.',
    destino: '/estoque',
    icone: 'Boxes',
    chip: 3,
    textoSemMedicao: 'Nenhum material tem estoque mínimo definido.',
  },
  'expedicoes-conferir': {
    rotulo: 'Expedições a conferir',
    definicao: 'Expedições abertas, com quantidade reservada ainda não coletada (a mesma contagem de "A expedir"). Só para quem pode consultar a expedição.',
    destino: '/expedicao',
    icone: 'Truck',
    chip: 3,
  },
  'pronto-para-faturar': {
    rotulo: 'Pronto para faturar',
    definicao:
      'Valor em reais dos lotes aprovados no CQ e ainda não faturados dos pedidos de venda prontos para fechar (quantidade pronta vezes o preço unitário do item no pedido). Itens sem preço ficam fora da soma e entram na cobertura. A faixa compara o faturado no mês (valor de serviços dos rascunhos fiscais aprovados) com o mesmo ponto do mês anterior.',
    destino: '/vendas/pedidos',
    icone: 'ClipboardCheck',
    rotuloSerie: 'faturamento diário · 14 dias úteis',
  },
  'pvs-prontos': {
    rotulo: 'PVs prontos para fechar',
    definicao:
      'Pedidos do cliente não cancelados com ao menos um lote aprovado no CQ, com a OP concluída no Kanban e quantidade ainda não faturada. Os itens aprovados no CQ são os itens de pedido desses lotes.',
    destino: '/vendas/pedidos',
    icone: 'ClipboardCheck',
    chip: 1,
    rotuloSerie: 'PVs fechados por dia · 14 dias úteis',
    textoApoio: (insumos) => {
      const itens = numeroSecundario(insumos, 'itens-aprovados-cq');
      if (itens === undefined) return undefined;
      return `${formatarNumero(itens)} ${itens === 1 ? 'item aprovado' : 'itens aprovados'} no CQ`;
    },
  },
  'notas-aguardando-aprovacao': {
    rotulo: 'Notas aguardando aprovação',
    definicao:
      'Notas de compra distintas aguardando aprovação fiscal ou financeira, só das aprovações que o seu perfil pode dar. Fiscais e financeiras são contadas à parte e nunca somadas.',
    // A lista de notas de compra abre também para quem aprova a proposta fiscal (PERMISSOES_ALTERNATIVAS_DA_ROTA).
    destino: '/compras/notas',
    icone: 'FileCheck',
    chip: 2,
    textoApoio: (insumos) => {
      const fiscais = numeroSecundario(insumos, 'fiscais');
      const financeiras = numeroSecundario(insumos, 'financeiras');
      const partes = [
        fiscais === undefined ? undefined : `${formatarNumero(fiscais)} ${fiscais === 1 ? 'fiscal' : 'fiscais'}`,
        financeiras === undefined ? undefined : `${formatarNumero(financeiras)} ${financeiras === 1 ? 'financeira' : 'financeiras'}`,
      ].filter((parte): parte is string => parte !== undefined);
      return partes.length === 0 ? undefined : partes.join(' · ');
    },
  },
  'omie-acao-necessaria': {
    rotulo: 'Omie · ação necessária',
    definicao:
      'Faturamentos que a Omie devolveu e precisam de ação (cadastro, vínculo ou revisão). O horário é a última vez em que um faturamento foi concluído na Omie.',
    destino: '/omie/pendencias',
    icone: 'AlertTriangle',
    chip: 3,
    alertaSeMaiorQueZero: true,
    textoApoio: (insumos) => {
      const iso = textoSecundario(insumos, 'ultima-sincronizacao');
      const hora = iso === undefined ? undefined : horaSaoPaulo(iso);
      return hora === undefined ? undefined : `demais sincronizados às ${hora}`;
    },
  },
  'faturado-mes': {
    rotulo: 'Faturado no mês',
    definicao:
      'Valor de serviços (mão de obra e tinta) dos rascunhos fiscais aprovados no mês corrente, do dia 1 até hoje. A barra de projeção soma o faturado, o pronto para faturar (lotes aprovados no CQ pelo preço do pedido de venda) e o saldo em produção valorizado pelo preço do pedido de venda; é uma soma, sem estimativa estatística. O fechamento do mês anterior fica ao lado para comparar. Só para quem pode ver valores.',
    destino: '/vendas/pedidos',
    icone: 'ClipboardCheck',
  },
  'pecas-devolvidas-mes': {
    rotulo: 'Peças devolvidas no mês',
    definicao:
      'Peças devolvidas aos clientes no mês corrente, do dia 1 até hoje: cada linha de retorno na data em que foi criada e, nas entregas por coleta, a coleta confirmada e não estornada. Quem não tem permissão para valores vê esta contagem no lugar do faturado, sem a barra de projeção.',
    destino: '/expedicao',
    icone: 'Truck',
  },
  'area-pintada-mes': {
    rotulo: 'Área pintada no mês',
    definicao:
      'Soma das peças apontadas nas cabines no mês corrente (boas e não conformes) multiplicada pela área unitária do item, a mesma regra da área pintada hoje. A média considera só os dias úteis (segunda a sexta, sem feriados) com apontamento de área cadastrada.',
    destino: '/monitoramento/visao-geral',
    icone: 'PaintBucket',
    chip: 2,
    textoApoio: (insumos) => {
      const media = numeroSecundario(insumos, 'media-dia-util');
      return media === undefined ? undefined : `média de ${formatarNumero(media, 0)} m² por dia útil`;
    },
  },
  'clientes-inadimplentes': {
    rotulo: 'Clientes inadimplentes',
    definicao:
      'Clientes ativos com inadimplência, bloqueio de crédito ou valor vencido a receber no espelho da Omie. O apoio soma o valor vencido desses clientes. Um número acima de zero pede atenção. Só para quem pode ver valores.',
    destino: '/cadastros',
    icone: 'AlertTriangle',
    chip: 3,
    alertaSeMaiorQueZero: true,
    textoApoio: (insumos) => {
      const valor = textoSecundario(insumos, 'em-aberto');
      if (valor === undefined) return undefined;
      // Cliente com restrição de crédito mas nada vencido: "R$ 0 em aberto" contradiz o número acima (D2 da UAT); diz o motivo da contagem.
      if (Number(valor) === 0) return 'com restrição de crédito';
      return `${formatarMoedaCompacta(valor)} em aberto`;
    },
  },
};

/** Id da métrica herói de cada home já migrada (os planos de perfil acrescentam o seu). */
export const METRICA_HEROI_DO_PERFIL: Readonly<Partial<Record<PerfilHome, string>>> = {
  pcp: 'area-pintada-hoje',
  almoxarifado: 'po-entregue-hoje',
  faturamento: 'pronto-para-faturar',
  qualidade: 'retrabalho-7d',
  compras: 'pedidos-em-aberto',
  diretoria: 'faturado-mes',
};

/**
 * Rótulo do herói da aba. Medido: o da métrica que a API devolveu (o Faturamento troca "Pronto para faturar" por "PVs prontos para
 * fechar" sem a permissão de valores). Sem número (carregando, indisponível, sem medição): o do perfil e, no Faturamento, o que a
 * permissão `financeiro:consultar` da sessão diria.
 */
export function rotuloDoHeroi(perfil: PerfilHome, heroi: MetricaHeroi | undefined, permissoes: readonly string[]): string {
  const comPeriodo = (entrada: EntradaMetrica | undefined): string | undefined =>
    entrada === undefined ? undefined : entrada.sufixoPeriodo === undefined ? entrada.rotulo : `${entrada.rotulo} ${entrada.sufixoPeriodo}`;
  // Diretoria: "Faturado em {mês}" com o mês do período medido; sem número, o rótulo que a permissão de valores da sessão diria.
  if (perfil === 'diretoria' && heroi?.estado === 'MEDIDO' && heroi.id === 'faturado-mes') return `Faturado em ${nomeDoMes(heroi.periodo.inicio.slice(0, 7))}`;
  if (perfil === 'diretoria' && !permissoes.includes('financeiro:consultar')) return comPeriodo(CATALOGO_METRICAS['pecas-devolvidas-mes']) ?? 'Peças devolvidas no mês';
  if (heroi?.estado === 'MEDIDO') {
    const rotulo = comPeriodo(CATALOGO_METRICAS[heroi.id]);
    if (rotulo !== undefined) return rotulo;
  }
  const id = perfil === 'faturamento' && !permissoes.includes('financeiro:consultar') ? 'pvs-prontos' : METRICA_HEROI_DO_PERFIL[perfil];
  return (id !== undefined ? comPeriodo(CATALOGO_METRICAS[id]) : undefined) ?? 'Indicador principal';
}
